import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import consultantApi from '../../services/consultantApi';
import './ConsultantProfile.css';

const ConsultantProfile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    phoneCode: '+91',
    companyName: '',
    bio: '',
    website: '',
    location: { city: '', state: '', country: '' }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await consultantApi.getProfile();
      const data = response.data.data || response.data;
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        phoneCode: data.phoneCode || '+91',
        companyName: data.companyName || '',
        bio: data.bio || '',
        website: data.website || '',
        location: {
          city: data.location?.city || '',
          state: data.location?.state || '',
          country: data.location?.country || ''
        }
      });
    } catch (error) {
      toast.error('Failed to load profile');
      console.error('Fetch profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('location.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({ ...prev, location: { ...prev.location, [key]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await consultantApi.updateProfile(formData);
      const updated = response.data.data || response.data;
      toast.success('Profile updated successfully!');
      if (updateUser) updateUser(updated);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update profile');
      console.error('Update profile error:', error);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'basic', label: 'Basic Info', icon: 'person' },
    { key: 'company', label: 'Company Details', icon: 'business' },
    { key: 'contact', label: 'Contact & Location', icon: 'location_on' }
  ];

  if (loading) {
    return (
      <div className="cp-container">
        <div className="cp-loading">
          <div className="cp-spinner"></div>
          <p>Loading profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-container">
      {/* Header */}
      <div className="cp-header">
        <div className="cp-header-left">
          <div className="cp-avatar">
            <span className="material-symbols-outlined">person</span>
          </div>
          <div>
            <h1 className="cp-name">{formData.name || 'Consultant'}</h1>
            <p className="cp-role">
              <span className="material-symbols-outlined">work</span>
              {formData.companyName ? formData.companyName : 'Independent Consultant'}
            </p>
            {formData.location?.city && (
              <p className="cp-location">
                <span className="material-symbols-outlined">location_on</span>
                {[formData.location.city, formData.location.state, formData.location.country].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="cp-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`cp-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            <span className="material-symbols-outlined">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      <form className="cp-form" onSubmit={handleSave}>
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="cp-card">
            <h2 className="cp-card-title">
              <span className="material-symbols-outlined">person</span>
              Basic Information
            </h2>
            <div className="cp-grid">
              <div className="cp-field">
                <label className="cp-label">Full Name <span>*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="cp-input"
                  placeholder="Your full name"
                  required
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="cp-input"
                  placeholder="your@email.com"
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">Phone Number</label>
                <div className="cp-phone-row">
                  <select
                    name="phoneCode"
                    value={formData.phoneCode}
                    onChange={handleChange}
                    className="cp-select cp-phone-code"
                  >
                    <option value="+91">+91 (IN)</option>
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+971">+971 (UAE)</option>
                    <option value="+61">+61 (AU)</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="cp-input"
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>
            <div className="cp-field cp-field-full">
              <label className="cp-label">Bio / About Me</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                className="cp-textarea"
                rows={5}
                placeholder="Tell employers about your expertise, skills, and what you specialize in..."
              />
              <p className="cp-hint">{formData.bio.length}/500 characters</p>
            </div>
          </div>
        )}

        {/* Company Details Tab */}
        {activeTab === 'company' && (
          <div className="cp-card">
            <h2 className="cp-card-title">
              <span className="material-symbols-outlined">business</span>
              Company / Agency Details
            </h2>
            <div className="cp-info-box">
              <span className="material-symbols-outlined">info</span>
              <p>Add your company or agency name here. When posting jobs, you can choose to post under this name or on behalf of associated companies.</p>
            </div>
            <div className="cp-grid">
              <div className="cp-field">
                <label className="cp-label">Company / Agency Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="cp-input"
                  placeholder="e.g. Winggs Recruitment Services"
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">Website</label>
                <div className="cp-input-icon">
                  <span className="material-symbols-outlined">language</span>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="cp-input"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>

            {/* Associated Companies Summary */}
            <div className="cp-section-divider">
              <span>Associated Companies</span>
            </div>
            <div className="cp-info-box cp-info-purple">
              <span className="material-symbols-outlined">corporate_fare</span>
              <p>Manage your company associations from the <a href="/consultant/companies">Companies page</a>. Accepted associations allow you to post jobs on behalf of those companies.</p>
            </div>
          </div>
        )}

        {/* Contact & Location Tab */}
        {activeTab === 'contact' && (
          <div className="cp-card">
            <h2 className="cp-card-title">
              <span className="material-symbols-outlined">location_on</span>
              Contact & Location
            </h2>
            <div className="cp-grid cp-grid-3">
              <div className="cp-field">
                <label className="cp-label">City</label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleChange}
                  className="cp-input"
                  placeholder="e.g. Mumbai"
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">State</label>
                <input
                  type="text"
                  name="location.state"
                  value={formData.location.state}
                  onChange={handleChange}
                  className="cp-input"
                  placeholder="e.g. Maharashtra"
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">Country</label>
                <input
                  type="text"
                  name="location.country"
                  value={formData.location.country}
                  onChange={handleChange}
                  className="cp-input"
                  placeholder="e.g. India"
                />
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="cp-actions">
          <button type="submit" className="cp-save-btn" disabled={saving}>
            <span className="material-symbols-outlined">{saving ? 'hourglass_empty' : 'save'}</span>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsultantProfile;
